import { useState } from "react";
import { Edit2, Save, X, Calendar, DollarSign, Tag, User, Briefcase, Building2, MapPin, Mail, Phone, FileText } from "lucide-react";
import { enrichLead } from "@/lib/admin-api";
import { toast } from "sonner";
import { formatMtyDateTimeShort } from "@/lib/mty-date";

const FUENTES = ["cotizacion", "catalogo", "whatsapp", "directo", "kommo_import", "facebook", "google", "referido", "otro"];

export default function LeadInfoEditor({ lead, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    nombre: lead.nombre || "",
    telefono: lead.telefono || "",
    email: lead.email || "",
    empresa: lead.empresa || "",
    cargo: lead.cargo || "",
    tipo_espacio: lead.tipo_espacio || "",
    ciudad: lead.ciudad || "",
    origen_detallado: lead.origen_detallado || "",
    presupuesto_estimado: lead.presupuesto_estimado ?? "",
    responsable: lead.responsable || "",
    tags: (lead.tags || []).join(", "),
    next_followup_at: lead.next_followup_at ? lead.next_followup_at.slice(0, 16) : "",
  });

  const handleCancel = () => {
    setEditing(false);
    setForm({
      nombre: lead.nombre || "",
      telefono: lead.telefono || "",
      email: lead.email || "",
      empresa: lead.empresa || "",
      cargo: lead.cargo || "",
      tipo_espacio: lead.tipo_espacio || "",
      ciudad: lead.ciudad || "",
      origen_detallado: lead.origen_detallado || "",
      presupuesto_estimado: lead.presupuesto_estimado ?? "",
      responsable: lead.responsable || "",
      tags: (lead.tags || []).join(", "),
      next_followup_at: lead.next_followup_at ? lead.next_followup_at.slice(0, 16) : "",
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre || null,
        telefono: form.telefono || null,
        email: form.email || null,
        empresa: form.empresa || null,
        cargo: form.cargo || null,
        tipo_espacio: form.tipo_espacio || null,
        ciudad: form.ciudad || null,
        origen_detallado: form.origen_detallado || null,
        presupuesto_estimado: form.presupuesto_estimado !== "" ? Number(form.presupuesto_estimado) : null,
        responsable: form.responsable || null,
        tags: form.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        next_followup_at: form.next_followup_at ? new Date(form.next_followup_at).toISOString() : null,
      };
      await enrichLead(lead.id, payload);
      toast.success("Información actualizada");
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const fmtMoney = (n) => (n || n === 0 ? `$ ${Number(n).toLocaleString("es-MX", { maximumFractionDigits: 0 })} MXN` : "—");

  if (!editing) {
    return (
      <div className="bg-white border border-neutral-200 rounded-2xl p-5" data-testid="lead-info-view">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-base">Información del lead</h3>
          <button
            onClick={() => setEditing(true)}
            className="text-xs font-semibold text-red-600 hover:text-red-700 inline-flex items-center gap-1"
            data-testid="edit-lead-btn"
          >
            <Edit2 className="w-3 h-3" /> Editar
          </button>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
          <Row icon={User} label="Nombre" value={lead.nombre} />
          <Row icon={Phone} label="Teléfono" value={lead.telefono || "—"} />
          <Row icon={Mail} label="Email" value={lead.email || "—"} />
          <Row icon={Building2} label="Empresa" value={lead.empresa || "—"} />
          <Row icon={Briefcase} label="Cargo" value={lead.cargo || "—"} />
          <Row icon={MapPin} label="Ciudad" value={lead.ciudad || "—"} />
          <Row icon={FileText} label="Tipo de espacio" value={lead.tipo_espacio || "—"} />
          <Row icon={DollarSign} label="Presupuesto estimado" value={fmtMoney(lead.presupuesto_estimado)} />
          <Row icon={User} label="Responsable" value={lead.responsable || "—"} />
          <Row icon={Tag} label="Fuente" value={`${lead.fuente}${lead.origen_detallado ? ` · ${lead.origen_detallado}` : ""}`} />
          <Row icon={Calendar} label="Próximo seguimiento" value={lead.next_followup_at ? formatMtyDateTimeShort(lead.next_followup_at) : "—"} highlight={!!lead.next_followup_at} />
          <Row icon={Calendar} label="Último contacto" value={lead.last_contact_at ? formatMtyDateTimeShort(lead.last_contact_at) : "—"} />
        </dl>

        {lead.tags && lead.tags.length > 0 && (
          <div className="mt-4 pt-3 border-t border-neutral-100">
            <p className="text-xs font-bold text-neutral-500 uppercase tracking-wider mb-2">Etiquetas</p>
            <div className="flex flex-wrap gap-1.5">
              {lead.tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 px-2.5 py-1 bg-orange-50 text-orange-800 border border-orange-200 rounded-full text-xs font-semibold">
                  <Tag className="w-3 h-3" /> {t}
                </span>
              ))}
            </div>
          </div>
        )}

        {lead.mensaje && (
          <div className="mt-4 pt-3 border-t border-neutral-100">
            <p className="text-xs text-neutral-500 font-medium mb-1">Mensaje original</p>
            <p className="text-neutral-900 bg-neutral-50 rounded-lg p-3 text-sm">{lead.mensaje}</p>
          </div>
        )}
      </div>
    );
  }

  // Edit mode
  return (
    <div className="bg-white border-2 border-orange-300 rounded-2xl p-5" data-testid="lead-info-edit">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-base">Editar información del lead</h3>
        <div className="flex gap-2">
          <button onClick={handleCancel} disabled={saving} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-neutral-300 hover:bg-neutral-50 inline-flex items-center gap-1" data-testid="cancel-lead-edit">
            <X className="w-3 h-3" /> Cancelar
          </button>
          <button onClick={handleSave} disabled={saving} className="btn-primary px-3 py-1.5 rounded-lg text-xs inline-flex items-center gap-1 disabled:opacity-60" data-testid="save-lead-edit">
            <Save className="w-3 h-3" /> {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Nombre *">
          <input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="form-input w-full" data-testid="edit-nombre" />
        </Field>
        <Field label="Teléfono">
          <input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className="form-input w-full" inputMode="tel" data-testid="edit-telefono" />
        </Field>
        <Field label="Email">
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="form-input w-full" data-testid="edit-email" />
        </Field>
        <Field label="Empresa">
          <input value={form.empresa} onChange={(e) => setForm({ ...form, empresa: e.target.value })} className="form-input w-full" data-testid="edit-empresa" />
        </Field>
        <Field label="Cargo / Puesto">
          <input value={form.cargo} onChange={(e) => setForm({ ...form, cargo: e.target.value })} className="form-input w-full" placeholder="Ej: Director de mantenimiento" />
        </Field>
        <Field label="Ciudad">
          <input value={form.ciudad} onChange={(e) => setForm({ ...form, ciudad: e.target.value })} className="form-input w-full" />
        </Field>
        <Field label="Tipo de espacio">
          <select value={form.tipo_espacio} onChange={(e) => setForm({ ...form, tipo_espacio: e.target.value })} className="form-input w-full">
            <option value="">— Sin seleccionar —</option>
            {["estacionamiento", "industria", "escuela", "residencial", "deportivo", "comercial", "otro"].map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </Field>
        <Field label="Presupuesto estimado (MXN)">
          <input type="number" value={form.presupuesto_estimado} onChange={(e) => setForm({ ...form, presupuesto_estimado: e.target.value })} className="form-input w-full" placeholder="150000" />
        </Field>
        <Field label="Origen detallado">
          <select value={form.origen_detallado} onChange={(e) => setForm({ ...form, origen_detallado: e.target.value })} className="form-input w-full">
            <option value="">— Sin especificar —</option>
            {FUENTES.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </Field>
        <Field label="Responsable / Asesor">
          <input value={form.responsable} onChange={(e) => setForm({ ...form, responsable: e.target.value })} className="form-input w-full" placeholder="email del equipo" />
        </Field>
        <Field label="Próximo seguimiento">
          <input type="datetime-local" value={form.next_followup_at} onChange={(e) => setForm({ ...form, next_followup_at: e.target.value })} className="form-input w-full" />
        </Field>
        <Field label="Etiquetas (separadas por coma)" wide>
          <input
            value={form.tags}
            onChange={(e) => setForm({ ...form, tags: e.target.value })}
            className="form-input w-full"
            placeholder="caliente, solicitó muestra, vip"
            data-testid="edit-tags"
          />
        </Field>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, highlight }) {
  return (
    <div>
      <dt className="text-xs text-neutral-500 font-medium mb-0.5 flex items-center gap-1.5">
        <Icon className="w-3 h-3" /> {label}
      </dt>
      <dd className={`text-sm font-semibold ${highlight ? "text-orange-700" : "text-neutral-900"}`}>{value}</dd>
    </div>
  );
}

function Field({ label, wide, children }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <label className="block text-xs font-semibold text-neutral-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
